/**
 * WGS84 → GCJ-02 坐标转换工具
 * 用于将国际通用 WGS84 坐标系转换为中国高德地图使用的 GCJ-02 坐标系
 */

const PI = Math.PI;
const SEMI_MAJOR_AXIS = 6378245.0; // 长半轴
const ECCENTRICITY_SQ = 0.00669342162296594323; // 偏心率平方

/**
 * 判断坐标是否在中国范围外
 */
function outOfChina(lng, lat) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function transformLat(x, y) {
  let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(y * PI) + 40.0 * Math.sin(y / 3.0 * PI)) * 2.0 / 3.0;
  ret += (160.0 * Math.sin(y / 12.0 * PI) + 320 * Math.sin(y * PI / 30.0)) * 2.0 / 3.0;
  return ret;
}

function transformLng(x, y) {
  let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(x * PI) + 40.0 * Math.sin(x / 3.0 * PI)) * 2.0 / 3.0;
  ret += (150.0 * Math.sin(x / 12.0 * PI) + 300.0 * Math.sin(x / 30.0 * PI)) * 2.0 / 3.0;
  return ret;
}

/**
 * WGS84 坐标转 GCJ-02
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @returns {[number, number]} [转换后经度, 转换后纬度]
 */
export function wgs84ToGcj02(lng, lat) {
  if (outOfChina(lng, lat)) return [lng, lat];
  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);
  const radLat = lat / 180.0 * PI;
  let magic = Math.sin(radLat);
  magic = 1 - ECCENTRICITY_SQ * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / ((SEMI_MAJOR_AXIS * (1 - ECCENTRICITY_SQ)) / (magic * sqrtMagic) * PI);
  dLng = (dLng * 180.0) / (SEMI_MAJOR_AXIS / sqrtMagic * Math.cos(radLat) * PI);
  return [lng + dLng, lat + dLat];
}

/**
 * 深拷贝 GeoJSON 并将所有坐标从 WGS84 转换为 GCJ-02
 * @param {object} geojson - GeoJSON FeatureCollection
 * @returns {object} 转换后的 GeoJSON
 */
export function convertGeoJsonToGcj02(geojson) {
  const converted = JSON.parse(JSON.stringify(geojson));

  function convertCoords(coords) {
    if (typeof coords[0] === 'number') {
      const [newLng, newLat] = wgs84ToGcj02(coords[0], coords[1]);
      coords[0] = newLng;
      coords[1] = newLat;
    } else {
      coords.forEach(c => convertCoords(c));
    }
  }

  converted.features.forEach(f => {
    convertCoords(f.geometry.coordinates);
  });
  return converted;
}
